import { useEffect } from "react";

const BRAND = "Shreem Mantra Energy";
const SITE = "https://shreemmantraenergy.com";
const DEFAULT_OG = `${SITE}/og-image.jpg`;

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * title  — full <title> without the brand suffix (brand is appended)
 * jsonLd — a single schema.org object or an array of them
 * ogImage — absolute or root-relative image URL for social cards
 */
export function useSEO({ title, description, canonical, jsonLd, noindex, ogImage }) {
  useEffect(() => {
    const full = title ? `${title} | ${BRAND}` : BRAND;
    document.title = full;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:site_name", BRAND);
    upsertMeta("property", "og:title", full);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    const og = ogImage ? (ogImage.startsWith("http") ? ogImage : `${SITE}${ogImage}`) : DEFAULT_OG;
    upsertMeta("property", "og:image", og);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:image", og);
    upsertMeta("name", "twitter:title", full);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    const url = canonical
      ? `${SITE}${canonical}`
      : `${SITE}${window.location.pathname}`;
    upsertLink("canonical", url);
    upsertMeta("property", "og:url", url);

    document.querySelectorAll('script[data-sme-jsonld="true"]').forEach((n) => n.remove());
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    blocks.filter(Boolean).forEach((block) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.smeJsonld = "true";
      s.text = JSON.stringify(block);
      document.head.appendChild(s);
    });
  }, [title, description, canonical, noindex, ogImage, JSON.stringify(jsonLd)]);
}

export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: BRAND,
  url: SITE,
  logo: `${SITE}/favicon.svg`,
  address: {
    "@type": "PostalAddress",
    addressCountry: "IN",
  },
  sameAs: [
    "https://instagram.com/shreemmantraenergy",
    "https://youtube.com/@shreemmantraenergy",
    "https://facebook.com/shreemmantraenergy",
    "https://pinterest.com/shreemmantraenergy",
    "https://x.com/shreemmantraenergy",
  ],
};

export function breadcrumbSchema(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.label,
      item: `${SITE}${t.to || ""}`,
    })),
  };
}
